import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspaces(data.workspaces || []);
      const savedId = localStorage.getItem("fx_ws");
      const active = data.workspaces.find((w) => w.id === savedId) || data.workspaces[0] || null;
      setActiveWorkspace(active);
    } catch (_) {
      setUser(null); setWorkspaces([]); setActiveWorkspace(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (localStorage.getItem("fx_token")) refresh();
    else setLoading(false);
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token); await refresh(); return data;
  };
  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setToken(data.token); await refresh(); return data;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) { /* cookie may not exist */ }
    setToken(null); setUser(null); setWorkspaces([]); setActiveWorkspace(null);
  };
  const selectWorkspace = (ws) => { localStorage.setItem("fx_ws", ws.id); setActiveWorkspace(ws); };

  const roleRank = (r) => ({ viewer: 1, editor: 2, admin: 3, owner: 4 })[r] || 0;
  const role = activeWorkspace?.role || null;
  const canEdit = roleRank(role) >= 2;
  const canAdmin = roleRank(role) >= 3;
  const isOwner = role === "owner";

  return (
    <AuthCtx.Provider value={{ user, workspaces, activeWorkspace, loading, login, signup, logout, refresh, selectWorkspace, role, canEdit, canAdmin, isOwner }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
